import { AppConfig } from './../../app.config';
import { SnapshotService } from './../../snapshot/snapshot.service';
import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Projection, Correction } from '@liquitics/transaction';
import { CorrectionService } from './../correction.service';
import { CommunicationService } from '../../common/communication.service';
import { ProjectionService } from '../../projection/projection.service';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-projection-update',
    templateUrl: './projection-update.component.html',
    styleUrls: ['./projection-update.component.css']
})
export class ProjectionUpdateComponent implements OnInit {
    currencies: string[];
    directions: string[];
    clientGroups: string[] = [];
    productTypes: string[] = [];
    desks: string[] = [];
    submitResult: string;
    minimumCorrectionAmount = -10000000000;
    maximumCorrectionAmount = 10000000000;

    correctionFormGroup: FormGroup;

    constructor( @Inject(MAT_DIALOG_DATA) public data: any,
        private snapshotService: SnapshotService,
        private correctionService: CorrectionService,
        private communicationService: CommunicationService,
        private projectionService: ProjectionService,
        private appConfig: AppConfig) {
        this.currencies = this.appConfig.activeUserProfile.getCurrencies();
        this.clientGroups = snapshotService.applicationConfiguration.clientGroups;
        this.productTypes = snapshotService.applicationConfiguration.productTypes;
        this.desks = snapshotService.applicationConfiguration.desks;
        this.directions = ['PAY', 'REC'];

        this.correctionFormGroup = new FormGroup({
            'currency': new FormControl(this.data.correction.currency, [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(3),
                currencyValidator(this.currencies),
            ]
            ),
            'clientGroup': new FormControl(this.data.correction.projectionUpdateClientGroup, [
                Validators.required,
            ]
            ),
            'direction': new FormControl(this.data.correction.direction, [
                Validators.required,
            ]
            ),
            'productType': new FormControl(this.data.correction.projectionUpdateProductType, [
                Validators.required,
            ]
            ),
            'desk': new FormControl(this.data.correction.desk, [
                Validators.required,
            ]
            ),
            'startingAmount': new FormControl(this.data.projectionStartingAmount, [
            ]
            ),
            'currentAmount': new FormControl(this.data.projectionCurrentAmount, [
            ]
            ),
            'filledAmount': new FormControl(this.data.projectionFilledAmount, [
            ]
            ),
            'newAmount': new FormControl(this.data.correction.projectionUpdateNewAmount, [
                Validators.required,
                amountValidator(),
            ]
            ),
            'changeReason': new FormControl(this.data.correction.changeReason, [
                Validators.required,
            ]
            ),
        });
    }

    ngOnInit() {
        this.correctionFormGroup.get('startingAmount').disable();
        this.correctionFormGroup.get('currentAmount').disable();
        this.correctionFormGroup.get('filledAmount').disable();
    }

    submit() {
        const correction: Correction = this.data.correction;
        this.buildCorrection(correction);
        // Flip the sign as we force abs in the GUI
        if (correction.direction === 'PAY') {
            correction.projectionUpdateNewAmount = - Math.abs(correction.projectionUpdateNewAmount);
        } else {
            correction.projectionUpdateNewAmount = Math.abs(correction.projectionUpdateNewAmount);
        }
        if (!correction.projectionUpdateOriginalAmount) {
            correction.projectionUpdateOriginalAmount = 0.0;
        }
        correction.amount = correction.projectionUpdateNewAmount - correction.projectionUpdateOriginalAmount;
        this.correctionService
            .saveCorrection(this.data.correction)
            .then(() => {
                this.communicationService.openSnackBar('Submission Successful', 'Ok');
            })
            .catch((err: any) => {
                this.communicationService.openSnackBar('Submission Failed', 'Dismiss');
            })
            ;

    }

    get currency() {
        return this.correctionFormGroup.get('currency');
    }
    get direction() {
        return this.correctionFormGroup.get('direction');
    }
    get clientGroup() {
        return this.correctionFormGroup.get('clientGroup');
    }

    get productType() {
        return this.correctionFormGroup.get('productType');
    }

    get desk() {
        return this.correctionFormGroup.get('desk');
    }
    get newAmount() {
        return this.correctionFormGroup.get('newAmount');
    }
    get changeReason() {
        return this.correctionFormGroup.get('changeReason');
    }

    // updateCorrectionAmount(event: string) {
    //     const amount = parseFloat(event);
    //     this.data.correction.amount = amount;
    // }

    buildCorrection(correction: Correction) {
        correction.currency = this.currency.value;
        correction.direction = this.direction.value;
        correction.projectionUpdateClientGroup = this.clientGroup.value;
        correction.projectionUpdateProductType = this.productType.value;
        correction.desk = this.desk.value;
        correction.projectionUpdateNewAmount = this.newAmount.value;
        correction.changeReason = this.changeReason.value;
    }

    updateFormFields() {
        this.correctionFormGroup.setValue({
            'currency': this.data.correction.currency,
            'clientGroup': this.data.correction.projectionUpdateClientGroup,
            'direction': this.data.correction.direction,
            'productType': this.data.correction.projectionUpdateProductType,
            'desk': this.data.correction.desk,
            'startingAmount': this.data.projectionStartingAmount,
            'currentAmount': this.data.projectionCurrentAmount,
            'filledAmount': this.data.projectionFilledAmount,
            'newAmount': this.data.correction.projectionUpdateNewAmount,
            'changeReason': this.data.correction.changeReason,
        })
    }

    projectionUpdateKeyTypesSelected(event) {
        const correction: Correction = this.data.correction;
        this.buildCorrection(correction);

        correction.projectionUpdateOriginalAmount = null;
        this.data['projectionStartingAmount'] = null;
        this.data['projectionFilledAmount'] = null;
        this.data['projectionCurrentAmount'] = null;
        this.data['projectionChange'] = null;
        this.data['projectionCompletionPercentage'] = null;
        if (correction.projectionUpdateProductType && correction.desk) {
            this.projectionService.getProjection(this.appConfig.getTodayString(),
                correction.currency,
                correction.direction,
                correction.desk,
                correction.settleDate,
                correction.projectionUpdateProductType,
                correction.projectionUpdateClientGroup
            )
                .pipe(take(1))
                .toPromise()
                .then((projections: Projection[]) => {
                    if (projections.length > 0) {
                        correction.projectionUpdateOriginalAmount = projections[0].amount;
                        this.data['projectionStartingAmount'] = projections[0].startingAmount.toFixed(2);
                        this.data['projectionFilledAmount'] = projections[0].filledAmount.toFixed(2);
                        this.data['projectionCurrentAmount'] = projections[0].amount.toFixed(2);
                        this.data['projectionChange'] = (projections[0].amount - projections[0].startingAmount).toFixed(2);
                        this.data['projectionCompletionPercentage'] = (projections[0].filledAmount / projections[0].startingAmount) * 100;
                    }
                    this.updateFormFields();
                })
                .catch((err: any) => {
                    console.log(err);
                    this.communicationService.openSnackBar('Failed to setup Projection Update. Please try again.', 'Dismiss');
                })
                ;
        }
    }
}

export function currencyValidator(currencies: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        const isValid = currencies.includes(String(control.value).toUpperCase());
        return !isValid ? { 'invalidCurrency': { value: control.value } } : null;
    };
}
export function amountValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        const isValid = control.value > 0;
        return !isValid ? { 'invalidNewAmount': { value: control.value } } : null;
    };
}

